import { displayGroup } from "../../actions/session";
import { FormPathItem, FormTextItem } from "../../controls/FormFields";
import More from "../../controls/More";
import Toggle from "../../controls/Toggle";
import { Button } from "../../controls/TooltipControls";
import { addDiscoveredTool, setGameParameters, } from "../gamemode_management/actions/settings";
import * as selectors from "../../util/selectors";
import ToolIcon from "../../controls/ToolIcon";
import * as React from "react";
import { Col, ControlLabel, Form, FormControl, FormGroup, Modal, ToggleButton, ToggleButtonGroup, } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { toDirname, isEqual, toToolDiscovery, toEditStarter, updateImage, splitCommandLine, resolveToolName, } from "./util";
import { MainContext } from "../../views/MainWindow";
import { ProcessCanceled, UserCanceled } from "../../util/CustomErrors";
import { useSelector } from "react-redux";
import Environment from "./Environment";
export default function ToolEditDialog(props) {
    const { onClose, tool } = props;
    const { t } = useTranslation();
    const { game } = useSelector(mapStateToProps);
    const [imageId, setImageId] = React.useState(Date.now());
    const [editTool, setEditTool] = React.useState(toEditStarter(tool));
    const context = React.useContext(MainContext);
    const { onSetGameParameters, onAddTool, onEditEnv } = mapDispatchToProps(context.api.store.dispatch);
    let realName = editTool.name;
    if (realName === undefined && editTool !== undefined) {
        realName = editTool.name;
    }
    React.useEffect(() => {
        setEditTool(toEditStarter(tool));
    }, [tool, setEditTool]);
    let exePathPlaceholder = t("Target");
    const predefinedToolPath = (game?.supportedTools ?? []).find((st) => st.id === editTool.id)?.executable;
    if (predefinedToolPath !== undefined) {
        exePathPlaceholder = `../${predefinedToolPath}`;
    }
    const haveEnvironment = Object.keys(editTool.environment).length > 0;
    const handleChange = React.useCallback((field, value) => {
        setEditTool((oldState) => ({
            ...oldState,
            [field]: value,
        }));
    }, [editTool, setEditTool]);
    const addEnv = React.useCallback((key, value) => {
        const newEnv = {
            ...editTool.environment,
            [key]: value,
        };
        handleChange("environment", newEnv);
    }, [handleChange, editTool, setEditTool]);
    const removeEnv = React.useCallback((key) => {
        let newEnv = { ...editTool.environment };
        delete newEnv[key];
        handleChange("environment", newEnv);
    }, [handleChange, editTool]);
    const clearCache = () => {
        setImageId(Date.now());
    };
    const handleChangeParameters = React.useCallback((key, value) => {
        handleChange("commandLine", value);
    }, [handleChange]);
    const onShowError = React.useCallback((message, err, allowReport) => {
        context.api.showErrorNotification(message, err, { allowReport });
    }, [context]);
    const onUpdateImage = React.useCallback((filePath) => {
        updateImage(tool, filePath, (err) => {
            if (err !== null) {
                const reportable = !(err instanceof ProcessCanceled || err instanceof UserCanceled);
                if (reportable) {
                    onShowError("Failed to change tool icon", err, false);
                }
            }
            else {
                clearCache();
                handleChange("iconPath", tool.iconOutPath);
            }
        });
    }, [handleChange, clearCache, onShowError, tool]);
    const handleChangePath = React.useCallback((field, filePath) => {
        handleChange(field, filePath);
        if (!editTool.name) {
            handleChange("name", resolveToolName(editTool));
        }
        if (tool.logoName === undefined) {
            onUpdateImage(filePath);
        }
    }, [tool, handleChange, editTool, onShowError, onUpdateImage]);
    const toggleShell = React.useCallback(() => {
        handleChange("shell", !editTool.shell);
    }, [editTool, handleChange]);
    const toggleDetached = React.useCallback(() => {
        handleChange("detach", !editTool.detach);
    }, [editTool, handleChange]);
    const setStartEvent = React.useCallback((mode) => {
        editTool.onStart = mode === "nothing" ? undefined : mode;
        if (mode === "close" && !editTool.detach) {
            handleChange("detach", true);
        }
    }, [handleChange, editTool]);
    const onHandleChangeIcon = () => {
        context.api
            .selectFile({
            defaultPath: tool.exePath,
            title: t("Select image"),
            filters: [
                { name: "Images", extensions: ["png", "jpg", "ico"] },
                { name: "Executables", extensions: ["exe"] },
            ],
        })
            .then((filePath) => {
            if (filePath !== undefined) {
                onUpdateImage(filePath);
            }
        });
    };
    const saveAndClose = React.useCallback(() => {
        if (editTool.isGame) {
            const envCustomized = !isEqual(editTool.environment, tool.originalEnvironment);
            onSetGameParameters(editTool.gameId, {
                workingDirectory: editTool.workingDirectory,
                iconPath: editTool.iconPath,
                environment: editTool.environment,
                envCustomized,
                parameters: splitCommandLine(editTool.commandLine),
                shell: editTool.shell,
                detach: editTool.detach,
                onStart: editTool.onStart,
            });
        }
        else {
            onAddTool(editTool.gameId, editTool.id, toToolDiscovery(editTool));
        }
        onClose();
        context.api.events.emit("analytics-track-click-event", "Tools", "Edited tool");
    }, [editTool, onAddTool, onClose]);
    return (React.createElement(Modal, { show: true, onHide: onClose, id: "tool-edit-dialog" },
        React.createElement(Modal.Header, null,
            React.createElement(Modal.Title, null, realName)),
        React.createElement(Modal.Body, null,
            React.createElement(Form, { horizontal: true },
                React.createElement(FormTextItem, { t: t, controlId: "name", label: t("Name"), placeholder: t("Name"), stateKey: "name", value: realName, onChangeValue: handleChange, maxLength: 50 }),
                editTool.isGame ? (React.createElement(FormTextItem, { t: t, controlId: "target", label: t("Target"), placeholder: exePathPlaceholder, stateKey: "target", value: editTool.exePath, readOnly: editTool.isGame })) : (React.createElement(FormPathItem, { t: t, controlId: "target", label: t("Target"), placeholder: exePathPlaceholder, stateKey: "exePath", value: editTool.exePath, onChangeValue: handleChangePath, directory: false })),
                React.createElement(FormTextItem, { t: t, controlId: "cmdline", label: t("Command Line"), placeholder: t("Command Line Parameters"), stateKey: "commandLine", value: editTool.commandLine, onChangeValue: handleChangeParameters }),
                React.createElement(FormPathItem, { t: t, controlId: "workingdir", label: t("Start In"), placeholder: editTool.exePath !== undefined
                        ? toDirname(editTool.exePath)
                        : t("Select the executable first"), stateKey: "workingDirectory", value: editTool.workingDirectory, onChangeValue: handleChange, directory: true, readOnly: editTool.isGame }),
                React.createElement(FormGroup, { validationState: haveEnvironment ? "warning" : undefined },
                    React.createElement(Col, { sm: 3 },
                        React.createElement(ControlLabel, null, t("Environment Variables"))),
                    React.createElement(Col, { sm: 9 },
                        React.createElement(Environment, { t: t, addEnv: addEnv, environment: editTool.environment, onEditEnv: onEditEnv, removeEnv: removeEnv }),
                        haveEnvironment ? (React.createElement(ControlLabel, { style: { paddingTop: 0 } }, t('Tools with environments can\'t be started from the "Tasks" list'))) : null)),
                React.createElement(FormGroup, null,
                    React.createElement(Col, { sm: 3 },
                        React.createElement(ControlLabel, null, t("Icon"))),
                    React.createElement(Col, { sm: 9 },
                        React.createElement(FormControl.Static, null,
                            React.createElement(Button, { id: "change-tool-icon", tooltip: t("Change"), onClick: onHandleChangeIcon },
                                React.createElement(ToolIcon, { item: editTool, imageUrl: editTool.iconPath, imageId: imageId, valid: true }))))),
                React.createElement(FormGroup, null,
                    React.createElement(Col, { sm: 3 },
                        React.createElement(ControlLabel, null,
                            t("On Start"),
                            React.createElement(More, { id: "on-start", name: t("On Start behavior") }, t("Use these options if you don't want to leave the Vortex window " +
                                "open while the game is running. In some games this may be necessary " +
                                "to avoid performance problems.\n\n" +
                                'The "Close Vortex" option will close Vortex entirely, meaning it ' +
                                "will not use any system resources. This is not usually necessary.\n" +
                                "The hide options will hide Vortex to the system tray so the window " +
                                "isn't rendered at all.\n" +
                                'With the "restore when closed" option the Vortex window will ' +
                                "automatically reappear. Please be aware that this option will not " +
                                'work correctly if you start the game via a "proxy", e.g. the script ' +
                                "extenders for Elder Scrolls/Fallout games because Vortex will " +
                                "reappear as soon as the proxy closes.")))),
                    React.createElement(Col, { sm: 9 },
                        React.createElement(ToggleButtonGroup, { name: "onstart", type: "radio", value: editTool.onStart, onChange: setStartEvent },
                            React.createElement(ToggleButton, { value: "nothing" }, t("Nothing")),
                            React.createElement(ToggleButton, { value: "hide" }, t("Hide Vortex")),
                            React.createElement(ToggleButton, { value: "hide_recover" }, t("Hide Vortex, restore when closed")),
                            React.createElement(ToggleButton, { value: "close" }, t("Close Vortex"))))),
                React.createElement(FormGroup, null,
                    React.createElement(Col, { sm: 12 },
                        React.createElement(Toggle, { checked: editTool.shell, onToggle: toggleShell },
                            t("Run in shell"),
                            React.createElement(More, { id: "run-in-shell", name: t("Run in shell") }, t("If (and only if!) a tool is written as a console " +
                                "application, you have to enable this to allow it to run " +
                                "correctly."))))),
                React.createElement(FormGroup, null,
                    React.createElement(Col, { sm: 12 },
                        React.createElement(Toggle, { disabled: editTool.onStart === "close", checked: editTool.detach, onToggle: toggleDetached },
                            t("Run detached"),
                            React.createElement(More, { id: "run-detached", name: t("Run detached") }, t("Run the tool as a detached process (default). When you disable this " +
                                "the game/tool will run as a child process and thus if Vortex " +
                                "quits, the tool will also be terminated."))))))),
        React.createElement(Modal.Footer, null,
            React.createElement(Button, { id: "cancel-tool-btn", tooltip: t("Cancel"), onClick: onClose }, t("Cancel")),
            React.createElement(Button, { id: "apply-tool-btn", tooltip: t("Save"), onClick: saveAndClose }, t("Save")))));
}
// Not sure why this static variable is needed but whatever.
// ToolEditDialog.contextTypes = (props: IProps) => {
//   api: PropTypes.object.isRequired
// };
function mapStateToProps(state) {
    return {
        displayGroups: state.session.base.displayGroups,
        game: selectors.currentGame(state),
    };
}
function mapDispatchToProps(dispatch) {
    return {
        onAddTool: (gameId, toolId, result) => dispatch(addDiscoveredTool(gameId, toolId, result, true)),
        onEditEnv: (itemId) => dispatch(displayGroup("envEdit", itemId)),
        onSetGameParameters: (gameId, parameters) => dispatch(setGameParameters(gameId, parameters)),
    };
}
